import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Globe2, Zap, ShieldCheck, Cpu, Lock, Gauge, Factory, Droplets,
  Truck, Radio, Building2, Layers, TrendingDown, Server, KeyRound,
} from "lucide-react";
import SEO from "@/components/SEO";

const ARCHITECTURE = [
  { icon: <Globe2 size={22} />, title: "Non-steered independent core", text: "Not a standard consumer multi-IMSI profile. Devices sit on PTT NZ's own independent core network profile — fully non-steered, so hardware selects the mathematically strongest signal tier organically, with no artificial carrier throttling or intentional prioritisation." },
  { icon: <Zap size={22} />, title: "Local Breakouts — Auckland & Sydney", text: "Data exits locally in-country rather than routing overseas. That keeps latency under 40ms end-to-end — the difference between telemetry that's real-time and telemetry that's merely 'eventually consistent'." },
  { icon: <Cpu size={22} />, title: "LTE-M (Cat-M1) at the hardware layer", text: "Low-power wide-area connectivity with automatic multi-network failover built directly into the hardware — no application-layer polling required, and no meaningful battery drain from network switching." },
  { icon: <ShieldCheck size={22} />, title: "Real data-access monitoring", text: "Every SIM runs a smart applet that checks actual data throughput, not signal bars. A tower can show full signal while its backhaul is down — our SIMs detect that and swap networks until data is actually flowing again." },
];

const USE_CASES = [
  { icon: <Droplets size={20} />, title: "Utilities & smart metering", text: "Water, gas and electricity telemetry that stays connected through outages that take out the primary network." },
  { icon: <Factory size={20} />, title: "Industrial SCADA", text: "Remote control and monitoring for plant, pumping stations and processing infrastructure." },
  { icon: <Truck size={20} />, title: "Fleet & asset telemetry", text: "GPS, engine data and cargo condition monitoring across NZ and Australia on one SIM estate." },
  { icon: <Radio size={20} />, title: "Environmental & remote sensors", text: "Low-bandwidth, high-reliability links for sensors in locations a technician visits rarely, if ever." },
];

const AUDIENCE = [
  { icon: <Building2 size={16} />, label: "Utilities & energy operators" },
  { icon: <Factory size={16} />, label: "Industrial & manufacturing" },
  { icon: <Truck size={16} />, label: "Transport & logistics fleets" },
];

const PRICING_TIERS = [
  {
    id: "retail",
    icon: <Layers size={22} />,
    title: "Retail SIM Access",
    tag: "From $2.00 NZD/mo · $1.60 AUD/mo",
    text: "A flat, low monthly SIM access fee covering base connection infrastructure, with zero lock-in contracts.",
    points: ["NZ: from $2.00 NZD SIM access/mo, data from $0.06 NZD/MB", "AU: from $1.60 AUD SIM access/mo, data from $0.048 AUD/MB", "No bundles, no USD exchange-rate exposure"],
  },
  {
    id: "fleet",
    icon: <TrendingDown size={22} />,
    title: "High-Volume Fleet Tier",
    tag: "100+ active units",
    text: "Per-MB pricing scales down automatically as your fleet grows, without renegotiating a contract.",
    points: ["Scales down from $0.01 NZD/MB in New Zealand", "Scales down further for AU fleets too", "Add or remove SIMs over the air"],
    featured: true,
  },
  {
    id: "enterprise",
    icon: <Server size={22} />,
    title: "Enterprise & Utility Bids",
    tag: "Custom pricing",
    text: "Bespoke sub-cent per-MB frameworks engineered against your actual usage profile to beat rigid single-carrier contracts.",
    points: ["Volume-based custom quoting", "Backed by direct wholesale carrier relationships", "Built to out-compete direct corporate carrier plans"],
  },
];

const EnterpriseIoT = () => (
  <div data-testid="enterprise-iot-page">
    <SEO
      path="/solutions/enterprise-iot"
      title="Enterprise IoT — Non-Steered Multi-Network SIMs"
      description="Enterprise IoT connectivity: non-steered core network, native Local Breakouts in Auckland & Sydney, LTE-M failover, NIST-aligned security & SSO via Entra ID."
      keywords="enterprise IoT SIM New Zealand, enterprise IoT SIM Australia, critical infrastructure SIM failover, LTE-M Cat-M1 New Zealand, local breakout LBO Auckland Sydney, non-steered SIM network engineer, independent core network PLMN, industrial IoT connectivity NZ AU, SCADA cellular connectivity, utility telemetry SIM, business backup connectivity NZ AU, NIST compliant portal New Zealand, WCAG 2.2 AA accessible portal, SSO Microsoft Entra ID login"
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Enterprise & Industrial IoT Connectivity",
          provider: { "@type": "Organization", name: "PTT NZ Limited", url: "https://pttnz.net" },
          areaServed: ["New Zealand", "Australia"],
          description: "Non-steered, independent-core multi-network SIMs with native Local Breakouts in Auckland and Sydney, LTE-M hardware-level failover, and pure Pay As You Go per-MB pricing for enterprise and utility IoT fleets. The Connectivity Portal and platform are built to NIST-aligned security controls and WCAG 2.2 AA accessibility standards, with Single Sign-On (SSO) via Microsoft Entra ID for login and account management.",
          audience: { "@type": "BusinessAudience", audienceType: "Network engineers, CTOs, utilities, industrial IoT" },
          offers: [
            { "@type": "Offer", name: "Cellular IoT SIM Access — New Zealand", price: "2.00", priceCurrency: "NZD", areaServed: "New Zealand", description: "SIM access, per SIM per month, from." },
            { "@type": "Offer", name: "Cellular IoT SIM Access — Australia", price: "1.60", priceCurrency: "AUD", areaServed: "Australia", description: "SIM access, per SIM per month, from." },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How much does PTT NZ Cellular IoT cost per SIM per month?", acceptedAnswer: { "@type": "Answer", text: "In New Zealand, Cellular IoT SIM access starts from $2.00 NZD per SIM per month, with data from $0.06 NZD/MB. In Australia, Cellular IoT SIM access starts from $1.60 AUD per SIM per month, with data from $0.048 AUD/MB. Both scale down further for high-volume fleets of 100+ units." } },
            { "@type": "Question", name: "Is the PTT NZ Connectivity Portal compliant with security and accessibility standards?", acceptedAnswer: { "@type": "Answer", text: "Yes. The Connectivity Portal and underlying platform are built to NIST-aligned security controls and WCAG 2.2 AA accessibility standards, with Single Sign-On (SSO) via Microsoft Entra ID for login and account management." } },
          ],
        },
      ]}
    />

    {/* HERO */}
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">Enterprise & industrial IoT</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Built for network engineers, <span className="text-[var(--ptt-teal-deep)]">not just procurement.</span>
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl leading-relaxed">
          An independent, non-steered core network profile, native Local Breakouts in Auckland and Sydney, and hardware-level LTE-M failover — engineered for fleets where a dropped connection means a real-world outage, not just a support ticket.
        </p>
        <div className="mt-7 flex gap-3 flex-wrap">
          <Link to="/contact?interest=Enterprise%20IoT" data-testid="eiot-hero-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Talk to an engineer <ArrowRight size={14} /></Link>
          <Link to="/about#network-architecture" data-testid="eiot-hero-cta-network" className="ghost-button px-5 py-3 text-sm font-semibold inline-flex items-center gap-2">See the network architecture</Link>
        </div>
      </div>
    </section>

    {/* ARCHITECTURE */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">Technical architecture</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Four decisions that matter more than the SIM itself.</h2>
      <div className="grid sm:grid-cols-2 gap-5 mt-10" data-testid="eiot-architecture-grid">
        {ARCHITECTURE.map((a) => (
          <div key={a.title} className="bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`eiot-arch-${a.title.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`}>
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{a.icon}</div>
            <h3 className="font-display text-lg font-bold mt-5">{a.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{a.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* USE CASES */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-white/50">Where it's deployed</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Fleets where uptime isn't negotiable.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="eiot-use-cases-grid">
          {USE_CASES.map((u) => (
            <div key={u.title} className="border border-white/15 p-6" data-testid={`eiot-use-case-${u.title.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`}>
              <div className="w-10 h-10 grid place-items-center bg-white/10 text-[var(--ptt-teal)]">{u.icon}</div>
              <h3 className="font-display text-base font-bold mt-4">{u.title}</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{u.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <div className="eyebrow">Pay As You Go, engineered against data shock</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">A flat access fee, then pure consumption pricing.</h2>
        </div>
      </div>
      <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">
        Traditional single-carrier corporate plans lock you into rigid monthly data tiers priced in USD-linked wholesale rates — overage fees included. PTT NZ blends a low flat SIM access fee with pure per-MB consumption, fixed in NZD, so a fleet's bill tracks what it actually used, not a bundle it didn't. Figures below are indicative starting points — exact current plan pricing is always shown live on <Link to="/plans" className="underline hover:text-[var(--ptt-ink)]">our Plans page</Link>.
      </p>

      <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="eiot-pricing-tiers">
        {PRICING_TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`eiot-tier-${t.id}`}
            className={`relative flex flex-col p-6 border transition-colors ${t.featured ? "border-[var(--ptt-teal-deep)] bg-[var(--ptt-teal)]/[0.07]" : "border-[var(--ptt-line)] bg-white hover:border-[var(--ptt-ink)]"}`}
          >
            {t.featured && (
              <div className="absolute -top-3 left-4 bg-[var(--ptt-teal-deep)] text-white text-[10px] font-mono-tech uppercase tracking-widest px-2.5 py-1">Most fleets land here</div>
            )}
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{t.icon}</div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-teal-deep)] mt-4">{t.tag}</div>
            <h3 className="font-display text-lg font-bold mt-1.5">{t.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{t.text}</p>
            <ul className="mt-4 space-y-2 text-sm">
              {t.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[var(--ptt-ink-soft)]"><span className="text-[var(--ptt-teal-deep)] mt-1">•</span>{p}</li>
              ))}
            </ul>
            <Link
              to={`/contact?interest=${encodeURIComponent("Enterprise IoT")}&plan=${encodeURIComponent(t.title)}`}
              data-testid={`eiot-tier-cta-${t.id}`}
              className="mt-5 pt-4 border-t border-[var(--ptt-line)] text-sm font-semibold inline-flex items-center gap-1.5 signal-underline"
            >
              Get a custom quote <ArrowRight size={13} />
            </Link>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-[var(--ptt-line)] bg-[var(--ptt-paper)] p-7">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Built for organisations running critical infrastructure</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5" data-testid="eiot-audience">
          {AUDIENCE.map((a) => (
            <div key={a.label} className="flex items-center gap-2.5 text-sm font-medium">
              <span className="text-[var(--ptt-teal-deep)]">{a.icon}</span>{a.label}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* DATA SOVEREIGNTY */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow">Data sovereignty & compliance</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Your data never leaves the country it was generated in.</h2>
          <p className="mt-4 text-[var(--ptt-ink-soft)] leading-relaxed max-w-xl">
            Because every SIM breaks out locally — Auckland for New Zealand, Sydney for Australia — sensitive corporate and utility telemetry stays within domestic borders, supporting regulatory compliance frameworks in each country. There's no offshore hop to explain to an auditor. The Connectivity Portal and underlying platform are built to NIST-aligned security controls and WCAG 2.2 AA accessibility standards, with Single Sign-On (SSO) via Microsoft Entra ID for login and account management.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-[var(--ptt-ink)] bg-[var(--ptt-paper)] p-6 lg:p-8">
            <div className="flex items-center gap-2.5 mb-4">
              <Lock size={18} className="text-[var(--ptt-teal-deep)]" />
              <span className="font-mono-tech text-[11px] tracking-widest uppercase text-[var(--ptt-muted)]">Local breakout, end to end</span>
            </div>
            <ul className="space-y-3 text-sm text-[var(--ptt-ink-soft)]">
              <li className="flex items-start gap-3"><Gauge size={14} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />Under 40ms latency — no overseas backhaul</li>
              <li className="flex items-start gap-3"><Lock size={14} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />Data stays in NZ or AU, never routed overseas</li>
              <li className="flex items-start gap-3"><ShieldCheck size={14} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />Not exposed to permanent-roaming disconnection risk</li>
              <li className="flex items-start gap-3"><ShieldCheck size={14} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />NIST-aligned security &amp; WCAG 2.2 AA accessible portal</li>
              <li className="flex items-start gap-3"><KeyRound size={14} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />Single Sign-On (SSO) via Microsoft Entra ID</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
      <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Talk to us</div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Bring us your fleet spec, we&apos;ll price the network.</h3>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">Whether it's 20 sensors or 20,000, we&apos;ll size a Pay As You Go or bespoke enterprise pricing framework around your actual usage — not a wholesale bundle you'll outgrow.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/contact?interest=Enterprise%20IoT" data-testid="eiot-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to an engineer <ArrowRight size={16} /></Link>
            <Link to="/solutions" data-testid="eiot-final-cta-solutions" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Back to Solutions</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default EnterpriseIoT;
